// The numbers behind the admin and dealer dashboards: period windows, day
// buckets, and lead, view and listing summaries. Pure functions over docs
// already read, so the dashboards and their tests share one definition.
//
// Only events have a past: an enquiry, a view, a listing added. Current
// states (a lead's stage, a hidden listing, a reel's lifetime counters) have
// no history in Firestore, so nothing here compares them with last week.

const HOUR_MS = 60 * 60 * 1000;

export const PERIODS = [
  { id: "7d", days: 7, label: "7 days", before: "the 7 days before" },
  { id: "30d", days: 30, label: "30 days", before: "the 30 days before" },
];

// The lead stages dealers set, in pipeline order (the Leads page's list).
export const LEAD_STAGES = ["New", "Contacted", "Qualified", "Visit Scheduled", "Converted", "Closed"];

// Statuses older app builds wrote, read as the stage the app maps them to
// (src/features/leads/types/leadTypes.ts).
const LEGACY_STATUS = {
  "New Inquiry": "New",
  "Hot Lead": "Contacted",
  "Schedule Visit": "Visit Scheduled",
  Negotiation: "Qualified",
};

const CHANNELS = [
  { id: "call", label: "Calls" },
  { id: "chat", label: "Chats" },
  { id: "visit", label: "Visit requests" },
];

export function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Calendar arithmetic, so a day stays a day across clock changes.
export function addDays(dayStart, n) {
  const d = new Date(dayStart);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime();
}

/**
 * The current period (the last `days` days, today included), the period
 * before it, and the days the charts draw: two periods for a week, so last
 * week sits beside this one, and one period for a month.
 */
export function periodWindows(days, now = Date.now()) {
  const today = startOfDay(now);
  const start = addDays(today, -(days - 1));
  const seriesDays = days <= 7 ? days * 2 : days;
  const seriesStart = addDays(today, -(seriesDays - 1));
  const series = Array.from({ length: seriesDays }, (_, i) => {
    const dayStart = addDays(seriesStart, i);
    return {
      start: dayStart,
      end: addDays(dayStart, 1),
      isToday: i === seriesDays - 1,
      inPeriod: dayStart >= start,
    };
  });
  return {
    days,
    now,
    start,
    end: addDays(today, 1),
    prevStart: addDays(start, -days),
    prevEnd: start,
    series,
  };
}

export const countIn = (times, from, to) =>
  times.reduce((n, t) => (t !== null && t >= from && t < to ? n + 1 : n), 0);

export function bucketByDay(times, series) {
  const counts = series.map(() => 0);
  times.forEach((t) => {
    if (t === null) return;
    const i = series.findIndex((day) => t >= day.start && t < day.end);
    if (i >= 0) counts[i] += 1;
  });
  return series.map((day, i) => ({ ...day, count: counts[i] }));
}

export const leadStage = (lead) => {
  const status = lead.status || "New";
  return LEGACY_STATUS[status] || status;
};

/** When a lead came in: createdAt, else updatedAt (the Listings page's rule). */
export const leadArrivedAt = (lead) => toMillis(lead.createdAt) ?? toMillis(lead.updatedAt);
/** The lead's latest activity, which is when a New lead started waiting. */
export const leadActiveAt = (lead) => toMillis(lead.updatedAt) ?? toMillis(lead.createdAt);
// Leads written before leadType existed are direct, as the app and the rules read them.
const isNearbyLead = (lead) => lead.leadType === "nearby";

/** How long a New lead has waited, in the Listings rail's three steps. */
export function waitingAge(since, now) {
  const age = now - since;
  if (age < 2 * HOUR_MS) return "fresh";
  if (age < 24 * HOUR_MS) return "ageing";
  return "overdue";
}

/**
 * Lead numbers for a dealer from their consolidated leads (utils/leadUtils),
 * so every count matches the Leads page.
 */
export function summarizeLeads(leads, win) {
  const inPeriod = (lead) => {
    const t = leadArrivedAt(lead);
    return t !== null && t >= win.start && t < win.end;
  };
  const arrivals = leads.map(leadArrivedAt);
  const current = leads.filter(inPeriod);
  const nearby = current.filter(isNearbyLead).length;

  const waiting = leads
    .filter((lead) => leadStage(lead) === "New")
    .map((lead) => ({ lead, since: leadActiveAt(lead) ?? win.now }))
    .sort((a, b) => a.since - b.since)
    .map((entry) => ({ ...entry, age: waitingAge(entry.since, win.now) }));

  const channels = CHANNELS.map((channel) => ({
    ...channel,
    count: current.filter((lead) => {
      const acts = Array.isArray(lead.actions) && lead.actions.length ? lead.actions : [lead.type];
      return acts.includes(channel.id);
    }).length,
  }));

  return {
    current: current.length,
    previous: countIn(arrivals, win.prevStart, win.prevEnd),
    direct: current.length - nearby,
    nearby,
    series: bucketByDay(arrivals, win.series),
    waiting,
    pipeline: LEAD_STAGES.map((stage) => ({
      stage,
      count: leads.filter((lead) => leadStage(lead) === stage).length,
    })),
    channels,
  };
}

/**
 * View numbers from the view docs read for the chart's days. A month's chart
 * covers only the current period, so its previous total is counted on the
 * server and passed in.
 */
export function summarizeViews(views, win, previousCount = null) {
  const times = views.map((view) => toMillis(view.viewedAt));
  const byListing = new Map();
  views.forEach((view, i) => {
    const t = times[i];
    if (t === null || t < win.start || t >= win.end || !view.propertyId) return;
    byListing.set(view.propertyId, (byListing.get(view.propertyId) || 0) + 1);
  });
  return {
    current: countIn(times, win.start, win.end),
    previous: previousCount ?? countIn(times, win.prevStart, win.prevEnd),
    series: bucketByDay(times, win.series),
    byListing,
  };
}

// A listing's state; legacy listings without one are live, as the rules read them.
export const listingState = (listing) => listing.status || "active";

export function summarizeListings(listings, win) {
  const visible = listings.filter((listing) => listingState(listing) !== "deleted");
  const live = visible.filter((listing) => listingState(listing) === "active").length;
  const added = listings.map((listing) => toMillis(listing.createdAt));
  return {
    live,
    hidden: visible.length - live,
    added: {
      current: countIn(added, win.start, win.end),
      previous: countIn(added, win.prevStart, win.prevEnd),
    },
  };
}

/**
 * The listings drawing the most demand this period, ranked the way the app's
 * dealer home ranks them (an enquiry weighs three views), each with its last
 * 14 days of enquiries for the demand bars.
 */
export function topListings(listings, leads, viewsByListing, win, limit = 4) {
  const today = startOfDay(win.now);
  const bars = Array.from({ length: 14 }, (_, i) => {
    const start = addDays(today, i - 13);
    return { start, end: addDays(start, 1) };
  });
  const leadsByListing = new Map();
  leads.forEach((lead) => {
    if (!lead.propertyId) return;
    const list = leadsByListing.get(lead.propertyId) || [];
    list.push(leadArrivedAt(lead));
    leadsByListing.set(lead.propertyId, list);
  });

  return listings
    .filter((listing) => listingState(listing) !== "deleted")
    .map((listing) => {
      const arrivals = leadsByListing.get(listing.id) || [];
      const enquiries = countIn(arrivals, win.start, win.end);
      const views = viewsByListing.get(listing.id) || 0;
      return {
        listing,
        enquiries,
        views,
        score: enquiries * 3 + views,
        demand: bars.map((day) => countIn(arrivals, day.start, day.end)),
      };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function changeOf(current, previous) {
  const diff = current - previous;
  return { diff, direction: diff > 0 ? "up" : diff < 0 ? "down" : "flat" };
}

export const formatCount = (n) => Number(n || 0).toLocaleString("en-IN");

export const formatSigned = (n) => (n > 0 ? `+${formatCount(n)}` : n < 0 ? `-${formatCount(-n)}` : "0");
